/**
 * TABLE MERGE & BILL SPLIT FEATURE
 * Drop-in addition to TableOrderList.jsx
 *
 * Contains:
 *  1. MergeTableModal    — merges all unpaid items from one table into another
 *  2. SplitBillModal     — splits a table's items into two separate bills
 *
 * Usage: import these into your TableOrderList and render them inside the return,
 * passing the appropriate props (see JSDoc on each component).
 */

import React, { useState, useEffect, useMemo } from 'react';
import config from '../../config';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Paper,
  Divider,
  Chip,
  CircularProgress,
  IconButton,
  Alert,
  Tooltip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CloseIcon from '@mui/icons-material/Close';
import ReceiptIcon from '@mui/icons-material/Receipt';

// ─────────────────────────────────────────────────────────────────────────────
// HELPER
// ─────────────────────────────────────────────────────────────────────────────
const fmt = (n) => Number(n || 0).toFixed(2);

// ═════════════════════════════════════════════════════════════════════════════
// 1. MERGE TABLE MODAL
// ═════════════════════════════════════════════════════════════════════════════
/**
 * @param {Object}   props
 * @param {boolean}  props.open                 — controls visibility
 * @param {Function} props.onClose              — called when modal should close
 * @param {Array}    props.orders               — full list of table orders (from parent state)
 * @param {string}   props.token                — auth token
 * @param {Object}   props.company              — { id, ... }
 * @param {string}   props.currency             — display symbol e.g. "₹"
 * @param {Function} props.onSuccess            — called after successful merge (triggers parent refresh)
 */
export function MergeTableModal({
  open,
  onClose,
  orders,
  token,
  company,
  currency,
  onSuccess,
}) {
  const [step, setStep] = useState(0); // 0=select, 1=confirm, 2=success
  const [sourceTable, setSourceTable] = useState('');
  const [targetTable, setTargetTable] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Derive unique unpaid table numbers
  const unpaidTables = useMemo(() => {
    const map = {};
    orders.forEach((o) => {
      if (!o.isPaid && o.tableno) {
        if (!map[o.tableno])
          map[o.tableno] = { tableno: o.tableno, orders: [], total: 0 };
        map[o.tableno].orders.push(o);
        map[o.tableno].total += Number(o.finalPrice || 0);
      }
    });
    return Object.values(map).sort((a, b) =>
      String(a.tableno).localeCompare(String(b.tableno)),
    );
  }, [orders]);

  const sourceInfo = unpaidTables.find((t) => t.tableno === sourceTable);
  const targetInfo = unpaidTables.find((t) => t.tableno === targetTable);

  const reset = () => {
    setStep(0);
    setSourceTable('');
    setTargetTable('');
    setError('');
    setLoading(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleConfirm = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${config.API_BASE_URL}/home/MergeTables`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          sourceTableNo: sourceTable,
          targetTableNo: targetTable,
          companyId: company?.id,
        }),
      });
      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || 'Merge failed');
      }
      setStep(2);
      onSuccess();
    } catch (err) {
      setError(err.message || 'Table merge failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const steps = ['Select Tables', 'Confirm Merge', 'Done'];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth='sm'
      PaperProps={{ sx: { borderRadius: 3, boxShadow: 6 } }}>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          borderBottom: '1px solid #f0f0f0',
          pb: 2,
        }}>
        <MergeTypeIcon color='primary' />
        <Typography variant='h6' fontWeight={600}>
          Merge Tables
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <IconButton size='small' onClick={handleClose}>
          <CloseIcon fontSize='small' />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Stepper activeStep={step} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* ── STEP 0: SELECT ── */}
        {step === 0 && (
          <Box>
            <Alert severity='info' sx={{ mb: 3 }}>
              All <strong>unpaid</strong> items from the <em>source table</em>{' '}
              will move to the <em>target table</em>. The source table will be
              cleared.
            </Alert>

            {unpaidTables.length < 2 && (
              <Alert severity='warning'>
                Need at least 2 tables with unpaid orders to merge.
              </Alert>
            )}

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Source Table (move FROM)</InputLabel>
                  <Select
                    value={sourceTable}
                    label='Source Table (move FROM)'
                    onChange={(e) => {
                      setSourceTable(e.target.value);
                      setTargetTable('');
                    }}>
                    {unpaidTables.map((t) => (
                      <MenuItem key={t.tableno} value={t.tableno}>
                        Table {t.tableno} — {currency} {fmt(t.total)} (
                        {t.orders.length} order{t.orders.length > 1 ? 's' : ''})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth disabled={!sourceTable}>
                  <InputLabel>Target Table (merge INTO)</InputLabel>
                  <Select
                    value={targetTable}
                    label='Target Table (merge INTO)'
                    onChange={(e) => setTargetTable(e.target.value)}>
                    {unpaidTables
                      .filter((t) => t.tableno !== sourceTable)
                      .map((t) => (
                        <MenuItem key={t.tableno} value={t.tableno}>
                          Table {t.tableno} — {currency} {fmt(t.total)} (
                          {t.orders.length} order
                          {t.orders.length > 1 ? 's' : ''})
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Preview */}
            {sourceInfo && targetInfo && (
              <Paper
                variant='outlined'
                sx={{ mt: 3, p: 2, borderRadius: 2, bgcolor: '#f8fafc' }}>
                <Typography
                  variant='subtitle2'
                  color='text.secondary'
                  gutterBottom>
                  Merge Preview
                </Typography>
                <Box display='flex' alignItems='center' gap={2} flexWrap='wrap'>
                  <Chip
                    label={`Table ${sourceTable}: ${currency} ${fmt(sourceInfo.total)}`}
                    color='error'
                    variant='outlined'
                  />
                  <MergeTypeIcon color='action' />
                  <Chip
                    label={`Table ${targetTable}: ${currency} ${fmt(targetInfo.total)}`}
                    color='primary'
                    variant='outlined'
                  />
                  <Typography variant='body2' color='text.secondary'>
                    →
                  </Typography>
                  <Chip
                    label={`New Total: ${currency} ${fmt(sourceInfo.total + targetInfo.total)}`}
                    color='success'
                    variant='filled'
                  />
                </Box>
              </Paper>
            )}
          </Box>
        )}

        {/* ── STEP 1: CONFIRM ── */}
        {step === 1 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <WarningAmberIcon sx={{ fontSize: 56, color: 'warning.main' }} />
            </Box>
            <Typography
              variant='h6'
              align='center'
              gutterBottom
              fontWeight={600}>
              Confirm Table Merge
            </Typography>
            <Typography align='center' color='text.secondary' sx={{ mb: 3 }}>
              This action <strong>cannot be undone</strong>. All unpaid orders
              from Table <strong>{sourceTable}</strong> will be moved to Table{' '}
              <strong>{targetTable}</strong>.
            </Typography>
            <Paper variant='outlined' sx={{ p: 2, borderRadius: 2 }}>
              <Grid container spacing={1}>
                {[
                  ['Source Table', sourceTable],
                  ['Target Table', targetTable],
                  [
                    'Items Moving',
                    `${sourceInfo?.orders.length || 0} order(s)`,
                  ],
                  ['Amount Moving', `${currency} ${fmt(sourceInfo?.total)}`],
                  [
                    'New Combined Total',
                    `${currency} ${fmt((sourceInfo?.total || 0) + (targetInfo?.total || 0))}`,
                  ],
                ].map(([k, v]) => (
                  <React.Fragment key={k}>
                    <Grid item xs={6}>
                      <Typography variant='body2' color='text.secondary'>
                        {k}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography
                        variant='body2'
                        fontWeight={600}
                        align='right'>
                        {v}
                      </Typography>
                    </Grid>
                  </React.Fragment>
                ))}
              </Grid>
            </Paper>
            {error && (
              <Alert severity='error' sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </Box>
        )}

        {/* ── STEP 2: SUCCESS ── */}
        {step === 2 && (
          <Box textAlign='center' py={3}>
            <CheckCircleIcon
              sx={{ fontSize: 64, color: 'success.main', mb: 2 }}
            />
            <Typography variant='h6' fontWeight={600} gutterBottom>
              Merge Successful!
            </Typography>
            <Typography color='text.secondary'>
              Table {sourceTable} items have been merged into Table{' '}
              {targetTable}.
            </Typography>
            <Chip
              label={`Table ${sourceTable} is now free`}
              color='success'
              sx={{ mt: 2 }}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{ px: 3, py: 2.5, borderTop: '1px solid #f0f0f0', gap: 1 }}>
        {step === 0 && (
          <>
            <Button
              onClick={handleClose}
              color='inherit'
              sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Box sx={{ flexGrow: 1 }} />
            <Button
              variant='contained'
              color='primary'
              disabled={!sourceTable || !targetTable}
              onClick={() => setStep(1)}
              sx={{ textTransform: 'none', px: 3 }}>
              Next: Review
            </Button>
          </>
        )}
        {step === 1 && (
          <>
            <Button
              onClick={() => setStep(0)}
              color='inherit'
              sx={{ textTransform: 'none' }}
              disabled={loading}>
              Back
            </Button>
            <Box sx={{ flexGrow: 1 }} />
            <Button
              variant='contained'
              color='error'
              onClick={handleConfirm}
              disabled={loading}
              startIcon={
                loading ? (
                  <CircularProgress size={16} color='inherit' />
                ) : (
                  <MergeTypeIcon />
                )
              }
              sx={{ textTransform: 'none', px: 3 }}>
              {loading ? 'Merging...' : 'Confirm Merge'}
            </Button>
          </>
        )}
        {step === 2 && (
          <Button
            fullWidth
            variant='contained'
            onClick={handleClose}
            sx={{ textTransform: 'none' }}>
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// 2. SPLIT BILL MODAL
// ═════════════════════════════════════════════════════════════════════════════
/**
 * @param {Object}   props
 * @param {boolean}  props.open
 * @param {Function} props.onClose
 * @param {Object}   props.order               — the unpaid order to split
 * @param {string}   props.token
 * @param {Object}   props.company
 * @param {string}   props.currency
 * @param {Function} props.onSuccess
 */
export function SplitBillModal({
  open,
  onClose,
  order,
  token,
  company,
  currency,
  onSuccess,
}) {
  const [items, setItems] = useState([]); // all items on this order
  const [fetchingItems, setFetchingItems] = useState(false);
  const [splitMode, setSplitMode] = useState('equal'); // 'select' | 'equal'
  const [equalParts, setEqualParts] = useState(2);
  const [bill2Items, setBill2Items] = useState({}); // { itemId: qty }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState(0); // 0=configure, 1=confirm, 2=success
  const [newOrderId, setNewOrderId] = useState(null);

  // Load items when modal opens
  useEffect(() => {
    if (open && order?.id) {
      setFetchingItems(true);
      setItems([]);
      setBill2Items({});
      setStep(0);
      setError('');
      setSplitMode('equal');
      setEqualParts(2);

      // Split needs the RAW OrderDetail rows: GettableOrderDetails groups by item
      // and so carries no OrderDetail.Id, which is what the split matches on — the
      // id came through undefined and every select-split failed with
      // "Detail ID 0 not found". This endpoint returns the rows ungrouped, with
      // their real ids. Same item ordered twice now shows as two lines, which is
      // also what actually gets moved.
      fetch(`${config.API_BASE_URL}/restaurantorder/splititems/${order.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((r) => r.json())
        .then((data) =>
          setItems(
            Array.isArray(data)
              ? data.map((r) => ({
                  id: r.id,
                  name: r.subcategoryName
                    ? `${r.name} (${r.subcategoryName})`
                    : r.name,
                  qty: Number(r.qty || 0),
                  unit: Number(r.unitPrice || 0),
                  finalPrice: Number(r.lineTotal || 0),
                }))
              : [],
          ),
        )
        .catch(() => setError('Could not load order items.'))
        .finally(() => setFetchingItems(false));
    }
  }, [open, order?.id]);

  // ── bill2Items helpers ──
  const setBill2Qty = (itemId, delta, maxQty) => {
    setBill2Items((prev) => {
      const current = prev[itemId] || 0;
      const next = Math.max(0, Math.min(maxQty, current + delta));
      if (next === 0) {
        const { [itemId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [itemId]: next };
    });
  };

  // ── Compute totals ──
  // finalPrice is ALREADY the line total (unit × qty). Multiplying it by qty
  // again inflated every preview: a 100 order previewed as 120, so an equal
  // split showed 60/60 instead of 50/50. (The backend always wrote the correct
  // 50/50 — only this preview lied.)
  const calcItemTotal = (item) => Number(item.finalPrice || 0);
  // The API now supplies the real inclusive unit price; derive it only if absent.
  const unitPrice = (item) =>
    Number(item.unit || 0) > 0
      ? Number(item.unit)
      : Number(item.finalPrice || 0) / Math.max(1, Number(item.qty || 1));
  const orderTotal = items.reduce((s, it) => s + calcItemTotal(it), 0);

  // Bill 1 = remaining after moving to bill 2
  const bill1Items = useMemo(
    () =>
      items
        .map((it) => ({
          ...it,
          unit: unitPrice(it),
          qty: Number(it.qty || 1) - (bill2Items[it.id] || 0),
        }))
        .filter((it) => it.qty > 0),
    [items, bill2Items],
  );

  const bill2ItemsList = useMemo(
    () =>
      items
        .filter((it) => bill2Items[it.id])
        .map((it) => ({ ...it, unit: unitPrice(it), qty: bill2Items[it.id] })),
    [items, bill2Items],
  );

  const bill1Total = bill1Items.reduce((s, it) => s + it.unit * it.qty, 0);
  const bill2Total = bill2ItemsList.reduce((s, it) => s + it.unit * it.qty, 0);

  // Equal split: each part = orderTotal / equalParts
  const equalAmount = equalParts > 1 ? orderTotal / equalParts : orderTotal;

  const isBill2Empty = Object.keys(bill2Items).length === 0;
  const isBill1Empty = bill1Items.length === 0;

  const handleSplit = async () => {
    setLoading(true);
    setError('');
    try {
      const payload =
        splitMode === 'equal'
          ? {
              orderId: order.id,
              splitType: 'equal',
              parts: equalParts,
              companyId: company?.id,
              bill2Items: [],
            }
          : {
              orderId: order.id,
              splitType: 'select',
              bill2Items: Object.entries(bill2Items).map(([itemId, qty]) => ({
                itemId: Number(itemId),
                qty,
              })),
              parts: 0,
              companyId: company?.id,
            };


      const res = await fetch(`${config.API_BASE_URL}/home/SplitBill`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || 'Split failed');
      }
      const data = await res.json();
      setNewOrderId(data.newOrderId || null);
      setStep(2);
      onSuccess();
    } catch (err) {
      setError(err.message || 'Split bill failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  const steps = ['Configure Split', 'Confirm', 'Done'];

  // ── Bill Preview Card ──
  const BillCard = ({ label, itemsList, total, color }) => (
    <Paper
      variant='outlined'
      sx={{
        p: 2,
        borderRadius: 2,
        border: `1px solid`,
        borderColor: `${color}.light`,
        bgcolor: `${color === 'success' ? '#f0fdf4' : '#fff8f0'}`,
      }}>
      <Typography
        variant='subtitle2'
        color={`${color}.main`}
        fontWeight={700}
        gutterBottom>
        {label}
      </Typography>
      {itemsList.length === 0 ? (
        <Typography
          variant='body2'
          color='text.secondary'
          sx={{ fontStyle: 'italic' }}>
          No items
        </Typography>
      ) : (
        itemsList.map((it, i) => (
          <Box
            key={i}
            display='flex'
            justifyContent='space-between'
            alignItems='center'
            sx={{ py: 0.5 }}>
            <Typography variant='body2' sx={{ flexGrow: 1 }}>
              {it.name}
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mx: 1 }}>
              ×{it.qty}
            </Typography>
            <Typography variant='body2' fontWeight={500}>
              {currency} {fmt((it.unit ?? Number(it.finalPrice || 0)) * it.qty)}
            </Typography>
          </Box>
        ))
      )}
      <Divider sx={{ my: 1 }} />
      <Box display='flex' justifyContent='space-between'>
        <Typography variant='body2' fontWeight={600}>
          Total
        </Typography>
        <Typography variant='body2' fontWeight={700} color={`${color}.main`}>
          {currency} {fmt(total)}
        </Typography>
      </Box>
    </Paper>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth='md'
      PaperProps={{ sx: { borderRadius: 3, boxShadow: 6 } }}>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          borderBottom: '1px solid #f0f0f0',
          pb: 2,
        }}>
        <CallSplitIcon color='primary' />
        <Typography variant='h6' fontWeight={600}>
          Split Bill — Table {order?.tableno} (ORD_{order?.id})
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Chip
          label={`Total: ${currency} ${fmt(orderTotal)}`}
          color='primary'
          size='small'
        />
        <IconButton size='small' onClick={handleClose}>
          <CloseIcon fontSize='small' />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Stepper activeStep={step} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* ── STEP 0: CONFIGURE ── */}
        {step === 0 &&
          (fetchingItems ? (
            <Box textAlign='center' py={6}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              {/* Split Mode Selector */}
              <RadioGroup
                row
                value={splitMode}
                onChange={(e) => setSplitMode(e.target.value)}
                sx={{ mb: 3 }}>
                {/* <FormControlLabel
                  value='select'
                  control={<Radio />}
                  label='Select items manually'
                /> */}
                <FormControlLabel
                  value='equal'
                  control={<Radio />}
                  label='Split equally'
                />
              </RadioGroup>

              {/* ── EQUAL SPLIT ── */}
              {splitMode === 'equal' && (
                <Box>
                  <Alert severity='info' sx={{ mb: 2 }}>
                    The total amount will be divided equally. Each person pays{' '}
                    {currency} {fmt(equalAmount)}.
                  </Alert>
                  <Box
                    display='flex'
                    alignItems='center'
                    gap={2}
                    sx={{ mb: 3 }}>
                    <Typography>Number of splits:</Typography>
                    <IconButton
                      size='small'
                      onClick={() => setEqualParts((p) => Math.max(2, p - 1))}
                      disabled={equalParts <= 2}>
                      <RemoveIcon />
                    </IconButton>
                    <Typography
                      variant='h5'
                      fontWeight={700}
                      sx={{ minWidth: 40, textAlign: 'center' }}>
                      {equalParts}
                    </Typography>
                    <IconButton
                      size='small'
                      onClick={() => setEqualParts((p) => Math.min(10, p + 1))}
                      disabled={equalParts >= 10}>
                      <AddIcon />
                    </IconButton>
                  </Box>
                  <Grid container spacing={2}>
                    {Array.from({ length: equalParts }).map((_, idx) => (
                      <Grid item xs={6} sm={4} key={idx}>
                        <Paper
                          variant='outlined'
                          sx={{ p: 2, borderRadius: 2, textAlign: 'center' }}>
                          <Typography variant='caption' color='text.secondary'>
                            Bill {idx + 1}
                          </Typography>
                          <Typography
                            variant='h6'
                            fontWeight={700}
                            color='primary'>
                            {currency} {fmt(equalAmount)}
                          </Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {/* ── SELECT SPLIT ── */}
              {splitMode === 'select' && (
                <Grid container spacing={2}>
                  {/* Item Assignment Table */}
                  <Grid item xs={12} md={6}>
                    <Typography variant='subtitle2' gutterBottom>
                      Assign items to Bill 2
                    </Typography>
                    <Paper
                      variant='outlined'
                      sx={{ borderRadius: 2, overflow: 'hidden' }}>
                      <Table size='small'>
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#f1f5f9' }}>
                            <TableCell sx={{ fontWeight: 600 }}>Item</TableCell>
                            <TableCell align='center' sx={{ fontWeight: 600 }}>
                              Available
                            </TableCell>
                            <TableCell align='center' sx={{ fontWeight: 600 }}>
                              → Bill 2
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {items.map((item) => {
                            const maxQty = Number(item.qty || 1);
                            const b2qty = bill2Items[item.id] || 0;
                            return (
                              <TableRow key={item.id} hover>
                                <TableCell>
                                  <Typography variant='body2' fontWeight={500}>
                                    {item.name}
                                  </Typography>
                                  <Typography
                                    variant='caption'
                                    color='text.secondary'>
                                    {currency} {fmt(unitPrice(item))} each
                                  </Typography>
                                </TableCell>
                                <TableCell align='center'>
                                  <Chip
                                    label={maxQty}
                                    size='small'
                                    variant='outlined'
                                  />
                                </TableCell>
                                <TableCell align='center'>
                                  <Box
                                    display='flex'
                                    alignItems='center'
                                    justifyContent='center'
                                    gap={0.5}>
                                    <IconButton
                                      size='small'
                                      onClick={() =>
                                        setBill2Qty(item.id, -1, maxQty)
                                      }
                                      disabled={b2qty <= 0}>
                                      <RemoveIcon fontSize='small' />
                                    </IconButton>
                                    <Typography
                                      variant='body2'
                                      fontWeight={600}
                                      sx={{
                                        minWidth: 20,
                                        textAlign: 'center',
                                      }}>
                                      {b2qty}
                                    </Typography>
                                    <IconButton
                                      size='small'
                                      onClick={() =>
                                        setBill2Qty(item.id, +1, maxQty)
                                      }
                                      disabled={b2qty >= maxQty}>
                                      <AddIcon fontSize='small' />
                                    </IconButton>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </Paper>
                  </Grid>

                  {/* Live Preview */}
                  <Grid item xs={12} md={6}>
                    <Typography variant='subtitle2' gutterBottom>
                      Live Bill Preview
                    </Typography>
                    <Box display='flex' flexDirection='column' gap={2}>
                      <BillCard
                        label='Bill 1 (remains on table)'
                        itemsList={bill1Items}
                        total={bill1Total}
                        color='success'
                      />
                      <BillCard
                        label='Bill 2 (new separate bill)'
                        itemsList={bill2ItemsList}
                        total={bill2Total}
                        color='warning'
                      />
                    </Box>
                    {isBill2Empty && (
                      <Alert severity='info' sx={{ mt: 1 }}>
                        Assign at least one item to Bill 2
                      </Alert>
                    )}
                    {isBill1Empty && (
                      <Alert severity='warning' sx={{ mt: 1 }}>
                        Bill 1 would be empty — move at least one item back
                      </Alert>
                    )}
                  </Grid>
                </Grid>
              )}
            </Box>
          ))}

        {/* ── STEP 1: CONFIRM ── */}
        {step === 1 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <WarningAmberIcon sx={{ fontSize: 56, color: 'warning.main' }} />
            </Box>
            <Typography
              variant='h6'
              align='center'
              gutterBottom
              fontWeight={600}>
              Confirm Bill Split
            </Typography>
            <Typography align='center' color='text.secondary' sx={{ mb: 3 }}>
              A new order will be created. Taxes will be recalculated
              proportionally.
            </Typography>
            {splitMode === 'select' ? (
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <BillCard
                    label='Bill 1'
                    itemsList={bill1Items}
                    total={bill1Total}
                    color='success'
                  />
                </Grid>
                <Grid item xs={6}>
                  <BillCard
                    label='Bill 2 (new)'
                    itemsList={bill2ItemsList}
                    total={bill2Total}
                    color='warning'
                  />
                </Grid>
              </Grid>
            ) : (
              <Alert severity='info' icon={<CallSplitIcon />}>
                Splitting {currency} {fmt(orderTotal)} equally into{' '}
                <strong>{equalParts}</strong> bills of{' '}
                <strong>
                  {currency} {fmt(equalAmount)}
                </strong>{' '}
                each.
              </Alert>
            )}
            {error && (
              <Alert severity='error' sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </Box>
        )}

        {/* ── STEP 2: SUCCESS ── */}
        {step === 2 && (
          <Box textAlign='center' py={3}>
            <CheckCircleIcon
              sx={{ fontSize: 64, color: 'success.main', mb: 2 }}
            />
            <Typography variant='h6' fontWeight={600} gutterBottom>
              Split Successful!
            </Typography>
            <Typography color='text.secondary' gutterBottom>
              {splitMode === 'equal'
                ? `Order split into ${equalParts} equal bills.`
                : `Bill 2 created as a new order.`}
            </Typography>
            {newOrderId && (
              <Box
                sx={{
                  mt: 2,
                  display: 'flex',
                  gap: 1,
                  justifyContent: 'center',
                }}>
                <Button
                  variant='outlined'
                  size='small'
                  startIcon={<ReceiptIcon />}
                  onClick={() =>
                    window.open(
                      `${window.location.origin}/#/RestroInvoice/${newOrderId}`,
                      '_blank',
                    )
                  }>
                  View New Bill (ORD_{newOrderId})
                </Button>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{ px: 3, py: 2.5, borderTop: '1px solid #f0f0f0', gap: 1 }}>
        {step === 0 && (
          <>
            <Button
              onClick={handleClose}
              color='inherit'
              sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Box sx={{ flexGrow: 1 }} />
            <Button
              variant='contained'
              disabled={
                splitMode === 'select' ? isBill2Empty || isBill1Empty : false
              }
              onClick={() => setStep(1)}
              sx={{ textTransform: 'none', px: 3 }}>
              Next: Review
            </Button>
          </>
        )}
        {step === 1 && (
          <>
            <Button
              onClick={() => setStep(0)}
              color='inherit'
              disabled={loading}
              sx={{ textTransform: 'none' }}>
              Back
            </Button>
            <Box sx={{ flexGrow: 1 }} />
            <Button
              variant='contained'
              color='warning'
              onClick={handleSplit}
              disabled={loading}
              startIcon={
                loading ? (
                  <CircularProgress size={16} color='inherit' />
                ) : (
                  <CallSplitIcon />
                )
              }
              sx={{ textTransform: 'none', px: 3 }}>
              {loading ? 'Processing...' : 'Confirm Split'}
            </Button>
          </>
        )}
        {step === 2 && (
          <Button
            fullWidth
            variant='contained'
            onClick={handleClose}
            sx={{ textTransform: 'none' }}>
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
